import { useMemo, useState } from "react";
import {
  Typography,
  Card,
  CardHeader,
  CardBody,
  Checkbox,
  Chip,
  Tooltip as MTTooltip,
} from "@material-tailwind/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useCollection } from "react-firebase-hooks/firestore";
import { collection, orderBy, query } from "firebase/firestore";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ResponsiveContainer,
} from "recharts";
import db from "../firebase";
import {
  PARTNERS,
  OFF_FEED,
  GEO_BASES,
  REGIONS,
  NON_NETWORK_COLOR,
} from "./CirrusNetworkShareCard";

// Trend view of the Cirrus Network Share analysis, driven by the
// cirrus_network_snapshots collection: one precomputed data point per
// completed cirrus-feed scrape run (Mon/Wed/Fri), backfilled to the first
// feed run on 2026-07-06. Snapshots replay the live page's classification
// against the historical per-run scrape docs, so the latest point should
// match the Current view (small differences can come from scrape timing
// inside the 7-day window).

const GROUP_DEFS = [...PARTNERS, OFF_FEED];

const fmtDate = (dateKey) =>
  new Date(`${dateKey}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const fmtShare = (v) => (v == null || isNaN(v) ? "n/a" : `${v.toFixed(1)}%`);

function StatTile({ label, value, delta, foot }) {
  return (
    <div className="flex flex-col justify-center bg-white p-5">
      <Typography variant="small" className="uppercase text-[11px] tracking-wide text-blue-gray-500">
        {label}
      </Typography>
      <div className="mt-1 flex items-baseline gap-2">
        <Typography variant="h4" color="blue-gray">
          {value}
        </Typography>
        {delta != null && (
          <Typography
            variant="small"
            className={`font-medium ${delta > 0 ? "text-green-700" : delta < 0 ? "text-red-700" : "text-blue-gray-500"}`}
          >
            {delta > 0 ? "+" : ""}
            {delta}
          </Typography>
        )}
      </div>
      <Typography variant="small" color="blue-gray" className="mt-1">
        {foot}
      </Typography>
    </div>
  );
}

export function CirrusNetworkTrends() {
  const [geoBasis, setGeoBasis] = useState("country");
  const [region, setRegion] = useState("all");
  const [enabled, setEnabled] = useState({
    lone_mountain: true,
    aerista: true,
    cirrus_direct: true,
    off_feed: true,
  });

  const [snap, loading, error] = useCollection(
    useMemo(() => query(collection(db, "cirrus_network_snapshots"), orderBy("ts", "asc")), [])
  );

  // One chart row per snapshot under the current basis/region/channel
  // selection. Counts for a region a snapshot never saw default to zero.
  const series = useMemo(() => {
    if (!snap) return [];
    return snap.docs.map((d) => {
      const s = d.data();
      const counts = s.byBasis?.[geoBasis]?.[region] || { total: 0 };
      const row = {
        dateKey: s.dateKey,
        label: fmtDate(s.dateKey),
        total: counts.total || 0,
        feedCoverage: s.feedCoverage,
      };
      let network = 0;
      GROUP_DEFS.forEach((g) => {
        const n = counts[g.key] || 0;
        row[g.key] = n;
        row[`${g.key}_share`] = counts.total > 0 ? (100 * n) / counts.total : null;
        if (enabled[g.key]) network += n;
      });
      row.non_network = Math.max(0, row.total - GROUP_DEFS.reduce((sum, g) => sum + row[g.key], 0));
      row.network = network;
      row.networkShare = row.total > 0 ? (100 * network) / row.total : null;
      return row;
    });
  }, [snap, geoBasis, region, enabled]);

  // Compare the latest point against the one closest to 28 days earlier.
  const summary = useMemo(() => {
    if (series.length === 0) return null;
    const latest = series[series.length - 1];
    const targetMs = new Date(`${latest.dateKey}T00:00:00`).getTime() - 28 * 86400000;
    const prior = [...series]
      .reverse()
      .find((r) => new Date(`${r.dateKey}T00:00:00`).getTime() <= targetMs) || series[0];
    return { latest, prior, isSelf: prior === latest };
  }, [series]);

  const regionLabel = REGIONS.find((r) => r.key === region)?.label || region;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-2">
        <div className="flex items-center gap-2">
          <Typography variant="h5" color="blue-gray">
            Cirrus Network Trends
          </Typography>
          <MTTooltip
            className="max-w-xs"
            content="Each point replays the network-share analysis at a past cirrus-feed scrape (Mon/Wed/Fri), deduped by registration across all listing sites inside the same 7-day window as the Current view. History starts 2026-07-06, when the feed scrape began."
          >
            <InformationCircleIcon className="h-5 w-5 cursor-help text-blue-gray-300" />
          </MTTooltip>
        </div>
        {series.length > 0 && (
          <Chip
            size="sm"
            variant="ghost"
            color="blue-gray"
            value={`${series.length} snapshots · ${fmtDate(series[0].dateKey)} – ${fmtDate(series[series.length - 1].dateKey)}`}
          />
        )}
      </div>

      {loading && (
        <Typography variant="small" color="gray">
          Loading snapshots…
        </Typography>
      )}

      {error && (
        <Typography variant="small" color="red">
          Could not load snapshots: {error.code === "permission-denied"
            ? "permission denied — the firestore.rules entry for cirrus_network_snapshots has not been deployed yet (firebase deploy --only firestore:rules)."
            : error.message}
        </Typography>
      )}

      {!loading && !error && series.length === 0 && (
        <Typography variant="small" color="blue-gray">
          No trend snapshots found. The backfill script
          (functions/scripts/backfill_cirrus_network_snapshots.js) populates history, and the
          scheduled function adds a point after each feed scrape.
        </Typography>
      )}

      {series.length > 0 && (
        <>
          <div className="mb-6 flex flex-wrap items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-blue-gray-100 bg-white px-3 py-2">
              <Typography
                variant="small"
                className="whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-blue-gray-500"
              >
                Geography basis
              </Typography>
              <select
                className="cursor-pointer bg-transparent text-sm font-medium text-blue-gray-800 focus:outline-none"
                value={geoBasis}
                onChange={(e) => setGeoBasis(e.target.value)}
              >
                {GEO_BASES.map((b) => (
                  <option key={b.key} value={b.key}>{b.label}</option>
                ))}
              </select>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-blue-gray-100 bg-white px-3 py-2">
              <Typography
                variant="small"
                className="whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-blue-gray-500"
              >
                Region
              </Typography>
              <select
                className="cursor-pointer bg-transparent text-sm font-medium text-blue-gray-800 focus:outline-none"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              >
                {REGIONS.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </label>
            <div className="flex flex-wrap items-center gap-1 rounded-lg border border-blue-gray-100 bg-white px-2 py-0.5">
              {GROUP_DEFS.map((g) => (
                <label key={g.key} className="flex cursor-pointer items-center">
                  <Checkbox
                    checked={enabled[g.key]}
                    onChange={() => setEnabled((e) => ({ ...e, [g.key]: !e[g.key] }))}
                    containerProps={{ className: "p-1.5" }}
                  />
                  <span className="mr-1 h-2.5 w-2.5 rounded-full" style={{ backgroundColor: g.color }} />
                  <Typography variant="small" color="blue-gray" className="mr-2 text-[12px] font-medium">
                    {g.short}
                  </Typography>
                </label>
              ))}
            </div>
          </div>

          {summary && (
            <Card className="mb-6 overflow-hidden">
              <div className="grid grid-cols-2 gap-px bg-blue-gray-50 lg:grid-cols-4">
                <StatTile
                  label="Network share"
                  value={fmtShare(summary.latest.networkShare)}
                  foot={`latest (${summary.latest.label})`}
                />
                <StatTile
                  label="Share change"
                  value={
                    summary.isSelf || summary.latest.networkShare == null || summary.prior.networkShare == null
                      ? "n/a"
                      : `${summary.latest.networkShare - summary.prior.networkShare > 0 ? "+" : ""}${(summary.latest.networkShare - summary.prior.networkShare).toFixed(1)}pp`
                  }
                  foot={summary.isSelf ? "no earlier snapshot" : `vs ${summary.prior.label} (~4 weeks)`}
                />
                <StatTile
                  label="Network aircraft"
                  value={summary.latest.network}
                  delta={summary.isSelf ? null : summary.latest.network - summary.prior.network}
                  foot="across enabled channels"
                />
                <StatTile
                  label="On-market Cirrus"
                  value={summary.latest.total}
                  delta={summary.isSelf ? null : summary.latest.total - summary.prior.total}
                  foot={region === "all" ? "whole market" : regionLabel}
                />
              </div>
            </Card>
          )}

          <Card className="mb-6">
            <CardHeader floated={false} shadow={false} color="transparent" className="m-0 p-6 pb-0">
              <Typography variant="h6" color="blue-gray">
                Network Share Over Time
                {region !== "all" ? ` (${regionLabel})` : ""}
              </Typography>
              <Typography variant="small" color="gray">
                Bold line: enabled channels combined, as % of on-market Cirrus. Thin lines: each
                channel's own share of the market.
              </Typography>
            </CardHeader>
            <CardBody className="p-6 pt-4">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={series} margin={{ top: 5, right: 20, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eceff1" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#546e7a" }} minTickGap={30} />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#546e7a" }}
                    tickFormatter={(v) => `${v}%`}
                    domain={[0, (dataMax) => Math.min(100, Math.ceil((dataMax + 5) / 5) * 5)]}
                  />
                  <Tooltip formatter={(v, name) => [fmtShare(v), name]} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    type="monotone"
                    dataKey="networkShare"
                    name="Network (enabled)"
                    stroke="#263238"
                    strokeWidth={2.5}
                    dot={false}
                  />
                  {GROUP_DEFS.map((g) => (
                    <Line
                      key={g.key}
                      type="monotone"
                      dataKey={`${g.key}_share`}
                      name={g.label}
                      stroke={g.color}
                      strokeWidth={1.25}
                      strokeOpacity={enabled[g.key] ? 1 : 0.25}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          <Card>
            <CardHeader floated={false} shadow={false} color="transparent" className="m-0 p-6 pb-0">
              <Typography variant="h6" color="blue-gray">
                Market Composition Over Time
                {region !== "all" ? ` (${regionLabel})` : ""}
              </Typography>
              <Typography variant="small" color="gray">
                On-market Cirrus by channel (stacked aircraft counts). Grey is everything outside
                the network.
              </Typography>
            </CardHeader>
            <CardBody className="p-6 pt-4">
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={series} margin={{ top: 5, right: 20, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eceff1" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#546e7a" }} minTickGap={30} />
                  <YAxis tick={{ fontSize: 11, fill: "#546e7a" }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {GROUP_DEFS.map((g) => (
                    <Area
                      key={g.key}
                      type="monotone"
                      stackId="mkt"
                      dataKey={g.key}
                      name={g.label}
                      stroke={g.color}
                      fill={g.color}
                      fillOpacity={0.75}
                      hide={!enabled[g.key]}
                    />
                  ))}
                  <Area
                    type="monotone"
                    stackId="mkt"
                    dataKey="non_network"
                    name="Non-network"
                    stroke={NON_NETWORK_COLOR}
                    fill={NON_NETWORK_COLOR}
                    fillOpacity={0.45}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}

export default CirrusNetworkTrends;
